/**
 * Photos et documents envoyés par l'utilisateur (T11) — décisions du 2026-10-17.
 *
 * Chaque photo est compressée dans le navigateur avant l'envoi (≈ 1 600 px, JPEG) : une photo de téléphone de
 * 3 à 8 Mo devient quelques centaines de Ko. Les limites reprennent celles du serveur (`backend/images.py`), qui
 * garantissent qu'aucun document MongoDB ne dépasse 16 Mo. Une liste ne reçoit que le nombre de photos d'une
 * annonce (`photo_count`) : `photoCount()` le lit, ou compte les photos quand l'annonce entière est là.
 */

export const PHOTO_MAX_SIDE = 1600;
export const DOCUMENT_MAX_SIDE = 2000;            // pièce d'identité : texte lisible
export const PHOTO_DATA_URL_MAX = 2_000_000;      // = MAX_PHOTO_DATA_URL (serveur)
export const DOCUMENT_DATA_URL_MAX = 3_000_000;   // = MAX_DOCUMENT_DATA_URL (serveur)
const QUALITIES = [0.82, 0.7, 0.55];

export class PhotoError extends Error {}

/** Dimensions qui tiennent dans un carré de `maxSide` pixels, proportions gardées, jamais agrandies. */
export function fitWithin(width, height, maxSide) {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Nombre de photos d'une annonce, qu'elle vienne d'une liste (`photo_count`) ou de sa page (`photos`). */
export function photoCount(shipment) {
  if (!shipment) return 0;
  if (Array.isArray(shipment.photos)) return shipment.photos.length;
  return Number(shipment.photo_count) || 0;
}

export function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new PhotoError('Ce fichier n’a pas pu être lu.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new PhotoError('Format non pris en charge : choisissez une photo JPEG, PNG ou WebP.'));
    };
    img.src = url;
  });
}

/**
 * Photo compressée en data URL JPEG. `maxSide` : côté le plus long ; `maxLength` : longueur maximale de la data URL.
 * La qualité baisse par paliers si la photo reste trop lourde.
 */
export async function compressImage(file, { maxSide = PHOTO_MAX_SIDE, maxLength = PHOTO_DATA_URL_MAX } = {}) {
  if (!file?.type?.startsWith('image/')) throw new PhotoError('Ce fichier n’est pas une image.');
  const img = await loadImage(file);
  const { width, height } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height, maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';                       // fond blanc : la transparence d'un PNG deviendrait noire en JPEG
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  for (const quality of QUALITIES) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= maxLength) return dataUrl;
  }
  throw new PhotoError('Photo trop lourde, même compressée : choisissez une photo plus petite.');
}

/**
 * Pièce du dossier d'identité : une image est compressée (2 000 px, pour que le texte reste lisible) ; un PDF est
 * envoyé tel quel s'il pèse 2 Mo au plus.
 */
export async function prepareDocument(file) {
  if (file?.type === 'application/pdf') {
    const dataUrl = await readAsDataUrl(file);
    if (dataUrl.length > DOCUMENT_DATA_URL_MAX) throw new PhotoError('PDF trop volumineux : 2 Mo au plus.');
    return dataUrl;
  }
  return compressImage(file, { maxSide: DOCUMENT_MAX_SIDE, maxLength: DOCUMENT_DATA_URL_MAX });
}
